"use client"

import React from 'react'
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectProps,
    SelectTrigger,
    SelectValue,
} from "../ui/select"
import { cn } from '../../lib/utils'
import { InputGroupProps } from './input-group'
import FormTextInputWrapper from '../form-inputs/form-input-wrapper'
import { workScopeStatusOptions, overallStatusOptions, potentialStructuralProblemsOptions, isNewHousingHelpNeededOptions, residentTypeOptions } from '../../../features/homeowners/constants/options'

export type SelectOption = {
    label: string | React.ReactNode,
    value: string,
}

/** Coerce controlled values to the string keys Radix Select + options use (fixes number vs "5" mismatch). */
function toSelectValueKey(value: string | number | undefined | null): string {
    if (value === null || value === undefined || value === "") return ""
    return String(value)
}

type InputHandleProps = {
    value?: string | number,
    defaultValue?: string | number,
    // ref?: RefObject<HTMLButtonElement>,
    ref?: React.LegacyRef<HTMLButtonElement>,
    onChange?: (value: string) => void,
    placeholder?: string,
    options: readonly SelectOption[],
    leftIcon?: React.ReactNode,
    /** When true, placeholder is shown (value is treated as empty for display) and styled muted/transparent */
    loading?: boolean,
}

export type SelectInputProps =
    Omit<SelectProps, keyof InputHandleProps> &
    InputHandleProps &
    {
        /** Label supports string or ReactNode (e.g. custom label with toggle) */
        inputGroupProps?: Omit<InputGroupProps, 'label'> & { label?: React.ReactNode },
        /** Optional class for the placeholder text (e.g. match label font, more transparent) */
        placeholderClassName?: string,
        /** When value equals this, show placeholder-style text (e.g. for "Select" option with value "all") */
        emptyValue?: string,
    }

const SelectInput = React.forwardRef<HTMLButtonElement, SelectInputProps>((
    props: SelectInputProps,
    ref
) => {

    const {
        value,
        onChange,
        defaultValue,
        name,
        disabled,
        placeholder,
        className,
        options,
        leftIcon,
        variant,
        size,
        onBlur,
        inputGroupProps,
        allowClear,
        onClear,
        loading,
        placeholderClassName,
        emptyValue,
    } = props

    const valueKey = toSelectValueKey(value)
    const defaultValueKey = toSelectValueKey(defaultValue)

    const showPlaceholderOnly =
        loading || (valueKey !== "" && !options.some((o) => o.value === valueKey))
    const showAsPlaceholder = showPlaceholderOnly || (emptyValue != null && valueKey === emptyValue)
    const placeholderDisplayText = showAsPlaceholder
        ? (emptyValue != null && valueKey === emptyValue
            ? (options.find(o => o.value === emptyValue)?.label ?? placeholder ?? "Select")
            : (placeholder || "Select"))
        : null
    const onValueChange = (value: string) => {
        if (onChange) {
            onChange(value)
        }
    }

    const isEmptyOrUnknown = valueKey === "" || valueKey === "Unknown"
    const isWorkScopeOrOverall = (options.some(option => workScopeStatusOptions.some(wsOption => wsOption.value === option.value)) ||
         options.some(option => overallStatusOptions.some(osOption => osOption.value === option.value))) &&
        !options.every(option => potentialStructuralProblemsOptions.some(psOption => psOption.value === option.value)) &&
        !options.every(option => isNewHousingHelpNeededOptions.some(nhOption => nhOption.value === option.value)) &&
        !options.every(option => residentTypeOptions.some(rtOption => rtOption.value === option.value))
    const showEmptyHighlight =
        (isEmptyOrUnknown && isWorkScopeOrOverall) || (inputGroupProps?.highlightWhenEmpty && valueKey === "")

    return (
        <FormTextInputWrapper
            inputGroupProps={inputGroupProps}
        >
            <Select
                disabled={disabled}
                name={name}
                onValueChange={onValueChange}
                value={valueKey === "" ? undefined : valueKey}
                defaultValue={defaultValueKey === "" ? undefined : defaultValueKey}
            >
                <SelectTrigger
                    disabled={disabled}
                    className={cn(
                        "data-[placeholder]:text-muted-foreground/60 relative transition-colors duration-200",
                        loading && showPlaceholderOnly && "data-[placeholder]:opacity-50 data-[placeholder]:italic",
                        inputGroupProps?.isDirty && "border-[#9c27b0] focus:border-[#9c27b0] focus:ring-[#9c27b0]/20 shadow-sm",
                        showEmptyHighlight && "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20",
                        className,
                    )}
                    style={
                        showEmptyHighlight
                            ? { backgroundColor: 'var(--color-pizza-600)', opacity: 0.3, color: '#000000' }
                            : undefined
                    }
                    variant={inputGroupProps?.error ? 'destructive' : 'default'}
                    size={size}
                    ref={ref}
                    onBlur={onBlur}
                    // Checking if allowClear is true, if it is, then we need to call the onClear function
                    // If it is not, then we need to call the onValueChange function, which clear the value to empty string
                    // This is because the onClear function is optional, and if it is not provided, then we need to call the onValueChange function,
                    onClear={!allowClear ? 
                        undefined : 
                        () => {
                            if (onClear) {
                                onClear()
                            } else {
                                onValueChange("")
                            }
                        }}
                    allowClear={allowClear}
                >
                    {/* Show Left Icon if provided */}
                    <div className="flex items-center gap-2">
                        {leftIcon}
                        {showAsPlaceholder && placeholderDisplayText != null ? (
                            <span className={cn(
                                "text-muted-foreground/60",
                                loading && showPlaceholderOnly && "opacity-50 italic",
                                placeholderClassName
                            )}>
                                {placeholderDisplayText}
                            </span>
                        ) : (
                            <SelectValue
                                placeholder={placeholder || "Select"}
                            />
                        )}
                    </div>
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup className='text-sm'>
                        {options.map((option) => (
                            <SelectItem
                                key={option.value}
                                value={option.value}
                                className='text-md'
                            >
                                {option.label}
                            </SelectItem>
                        ))}
                    </ SelectGroup>
                </SelectContent>
            </Select>
        </FormTextInputWrapper>
    )
})

SelectInput.displayName = 'SelectInput'

export default SelectInput
